/**
 * Turning a parsed SVG into the layer shapes the stitch renderer expects.
 *
 * The editor keeps two views of the same artwork. The *split* form gives each
 * layer one child per subpath, which is what satin rails and running stitch are
 * addressed against. The *joined* form collapses each layer back into a single
 * compound path, which is what gets filled and clipped. SvgUpdater has always
 * derived both, but as state setters tangled with React; these are the same
 * transforms as plain functions so the community preview can build the layers
 * without standing up an editor.
 */

// Stitch state used to be embedded in the SVG itself. It now lives in
// Firestore, but old files still carry the node and it must not be mistaken
// for a drawable layer.
const STITCH_STATE_ATTR = "data-svgtrace-stitch-state";

const isStitchStateMetadataNode = (c) =>
  c?.name === "metadata" &&
  c?.attributes &&
  Object.prototype.hasOwnProperty.call(c.attributes, STITCH_STATE_ATTR);

export const stripStitchMetadata = (svgJson) => {
  if (!svgJson?.children) return svgJson;
  const filtered = svgJson.children.filter((c) => !isStitchStateMetadataNode(c));
  if (filtered.length === svgJson.children.length) return svgJson;
  return { ...svgJson, children: filtered };
};

/**
 * Splits each layer's compound path into one child per subpath.
 * Drops nodes that are neither filled nor drawn, so stray defs and metadata
 * never shift the layer indices that stitch settings are keyed by.
 */
export function splitSvgLayers(parsed) {
  const clean = stripStitchMetadata(parsed);
  const layers = (clean?.children || []).filter(
    (c) => c?.attributes?.fill !== undefined || c?.attributes?.d !== undefined
  );

  const split = JSON.parse(JSON.stringify(clean));
  split.children = JSON.parse(JSON.stringify(layers));

  for (let i = 0; i < split.children.length; i++) {
    const compoundD = layers[i].attributes.d || "";
    split.children[i].attributes.d = "";
    split.children[i].children = [];

    for (const subpath of compoundD.split(/(?=[M])/)) {
      if (subpath === "") continue;
      split.children[i].children.push({
        name: "path",
        type: "element",
        attributes: { d: subpath },
        children: [],
      });
    }
  }

  return split;
}

/** Collapses each split layer back into a single compound path. */
export function joinSvgLayers(split) {
  const joined = JSON.parse(JSON.stringify(split));
  for (let i = 0; i < joined.children.length; i++) {
    let d = "";
    for (const child of split.children[i].children) d += child.attributes.d;
    joined.children[i].attributes.d = d;
    joined.children[i].children = [];
  }
  return joined;
}

/** Thread colour per layer. The layer's own fill is the source of truth. */
export function extractLayerColors(node) {
  return (node?.children || []).map((layer) => layer.attributes.fill);
}
